import {
  DEFAULT_STANDARDS_FRAMEWORK_ID,
  DEFAULT_STANDARDS_STAGE_ID,
} from './lotsogStandardsFrameworks.js';
import { DEFAULT_NARRATION_STYLE } from './lotsogGenerationConfig.js';

export const LOTSOG_CLASSES_COLLECTION = 'lotsogClasses';
export const LOTSOG_TEACHER_CURRICULA_COLLECTION = 'lotsogTeacherCurricula';
/** Singleton doc ids under users/{uid} (same pattern as session/current). */
export const LOTSOG_SUBSCRIPTION_DOC_ID = 'current';
export const LOTSOG_USAGE_CURRENT_PERIOD_DOC_ID = 'current';
/** Path suffix after users/{uid}/ — must have an even segment count for Firestore doc refs. */
export const LOTSOG_SUBSCRIPTION_DOC = `subscription/${LOTSOG_SUBSCRIPTION_DOC_ID}`;
export const LOTSOG_USAGE_CURRENT_PERIOD_DOC = `usageCurrentPeriod/${LOTSOG_USAGE_CURRENT_PERIOD_DOC_ID}`;
export const LOTSOG_USAGE_EVENTS_COLLECTION = 'usageEvents';
export const LOTSOG_TEACHER_STUDENT_INDEX_COLLECTION = 'lotsogTeacherStudentIndex';
export const LOTSOG_CLASS_INVITATION_INDEX_COLLECTION = 'lotsogClassInvitationIndex';
export const LOTSOG_TEACHER_INVITATIONS_COLLECTION = 'lotsogTeacherInvitations';
export const LOTSOG_ADMIN_INVITATIONS_COLLECTION = 'lotsogAdminInvitations';
export const LOTSOG_TRACKS_COLLECTION = 'lotsogLearningTracks';
export const LOTSOG_TRACK_JOBS_COLLECTION = 'lotsogLearningTrackJobs';
export const LOTSOG_TOPIC_PROGRESS_COLLECTION = 'lotsogLearningTopicProgress';
export const LOTSOG_LIVE_BRIEFING_SESSIONS_COLLECTION = 'lotsogLiveBriefingSessions';
export const LOTSOG_BARGE_IN_TRANSCRIPTS_COLLECTION = 'lotsogBargeInTranscripts';
export const LOTSOG_BARGE_IN_MODERATION_QUERY_ID = 'lotsog-barge-in-moderation';
export const LOTSOG_LIVE_BRIEFING_RECORDING_NOTICE_VERSION = 1;

/** @typedef {'active'|'ended'} LotsogLiveBriefingSessionStatus */
/** @typedef {'pause'|'resume'|'start_teacher_live'|'end_teacher_live'} LotsogLiveBriefingCommand */
/** @typedef {'observing'|'speaking'} LotsogLiveBriefingTeacherMode */

/**
 * @typedef {import('firebase/firestore').Timestamp} FirestoreTimestamp
 */

/**
 * @typedef {Object} EnrolledStudent
 * @property {string} uid
 * @property {string} displayName
 * @property {string} email
 * @property {FirestoreTimestamp} enrolledAt
 */

/**
 * @typedef {'pending'|'accepted'|'expired'|'revoked'} PendingInvitationStatus
 */

/**
 * @typedef {Object} PendingInvitation
 * @property {string} email
 * @property {string} token
 * @property {FirestoreTimestamp} sentAt
 * @property {FirestoreTimestamp} expiresAt
 * @property {PendingInvitationStatus} status
 */

/**
 * @typedef {Object} LotsogClass
 * User-facing canonical term: Section.
 * Stored in users/{uid}/lotsogClasses with id/key field classId.
 * @property {string} classId
 * @property {string} name
 * @property {string} description
 * @property {FirestoreTimestamp} createdAt
 * @property {FirestoreTimestamp} updatedAt
 * @property {string} teacherUid
 * @property {string} adminUid
 * @property {EnrolledStudent[]} enrolledStudents
 * @property {PendingInvitation[]} pendingInvitations
 */

/**
 * @typedef {'beginner'|'intermediate'|'advanced'} LotsogCurriculumLevel
 */
/**
 * @typedef {'beginner'|'intermediate'|'advanced'} LotsogProficiencyLevel
 */
/**
 * @typedef {'precise'|'natural'|'engaging'} LotsogNarrationStyle
 */
/**
 * @typedef {'every_lesson'|'every_n_lessons'|'per_unit'|'none'} LotsogQuizFrequency
 */
/**
 * @typedef {'multiple_choice'|'short_answer'|'mixed'} LotsogQuizType
 */
/**
 * @typedef {'test'|'project'|'presentation'|'essay'} LotsogFinalAssessmentType
 */
/**
 * @typedef {'on_grade'|'below_grade'|'above_grade'} LotsogReadingLevelAdjustment
 */

/**
 * @typedef {'ai_only'|'upload_only'|'upload_plus_ai'} LotsogCurriculumSourceMode
 */

/**
 * @typedef {'pending'|'ready'|'error'} CurriculumLessonStatus
 */

/**
 * @typedef {Object} ReviewQuizPreviewQuestion
 * @property {string} question
 * @property {string[]} options
 * @property {string} correct
 */

/**
 * @typedef {Object} ReviewQuizPreview
 * @property {ReviewQuizPreviewQuestion[]|null} questions
 * @property {FirestoreTimestamp|null} generatedAt
 */

/**
 * @typedef {'draft'|'generating_syllabus'|'generating_lessons'|'ready'|'awaiting_review'|'approved'|'needs_revision'|'error'} LotsogCurriculumStatus
 */

/**
 * @typedef {'pending'|'ready'|'failed'} LotsogBlueprintStatus
 */

/**
 * @typedef {Object} CurriculumSourceMetadata
 * @property {string|null} fileName
 * @property {string|null} fileUrl
 * @property {string|null} storagePath
 * @property {number|null} chunkCount
 * @property {FirestoreTimestamp|null} ingestedAt
 */

/**
 * @typedef {'pdf_outline'|'metadata_api'|'ai_extraction'|'manual'} LotsogSourceTocExtractionMethod
 */

/**
 * @typedef {'high'|'medium'|'low'} LotsogSourceTocConfidence
 */

/**
 * @typedef {Object} LotsogSourceTocSubchapter
 * @property {string} title
 * @property {number|null} pageStart
 */

/**
 * @typedef {Object} LotsogSourceTocChapter
 * @property {number|null} chapterNumber
 * @property {string} title
 * @property {number|null} pageStart
 * @property {number|null} pageEnd
 * @property {LotsogSourceTocSubchapter[]} subchapters
 * @property {number|null} suggestedLessonCount
 */

/**
 * @typedef {Object} LotsogSourceToc
 * @property {FirestoreTimestamp|null} extractedAt
 * @property {LotsogSourceTocExtractionMethod} extractionMethod
 * @property {LotsogSourceTocConfidence} confidence
 * @property {LotsogSourceTocChapter[]} chapters
 * @property {number} totalChapters
 */

/**
 * @typedef {Object} CurriculumSyllabus
 * @property {Record<string, string>} outline
 * @property {FirestoreTimestamp} generatedAt
 */

/**
 * @typedef {Object} LotsogSyllabusOverviewEntry
 * @property {number} day
 * @property {string} title
 */

/**
 * @typedef {Object} CurriculumLesson
 * User-facing canonical term: Lesson.
 * Stored key remains day in this pass.
 * @property {number} day
 * @property {string} title
 * @property {string} content
 * @property {string|null} keyConceptsSummary
 * @property {string|null} bloomTarget
 * @property {string|null} narrationSummary
 * @property {string|null} [narration]
 * @property {Object|null} [videoEnrichment]
 * @property {'youtube_crashcourse'} [videoEnrichment.platform]
 * @property {string} [videoEnrichment.videoId]
 * @property {string} [videoEnrichment.title]
 * @property {string} [videoEnrichment.url]
 * @property {string} [videoEnrichment.embedUrl]
 * @property {number} [videoEnrichment.durationSeconds]
 * @property {number} [videoEnrichment.durationMinutes]
 * @property {string} [videoEnrichment.thumbnailUrl]
 * @property {string} [videoEnrichment.subject]
 * @property {'early'|'mid'|'late'} [videoEnrichment.placement]
 * @property {number} [videoEnrichment.confidence]
 * @property {string} [videoEnrichment.resolvedAt]
 * @property {CurriculumLessonStatus} status
 * @property {number} [actualWords]
 * @property {boolean} [editedByTeacher]
 * @property {FirestoreTimestamp|null} [editedAt]
 * @property {FirestoreTimestamp|null} generatedAt
 * @property {FirestoreTimestamp|null} completedAt
 * @property {ReviewQuizPreview|null} [reviewQuizPreview]
 */

/**
 * @typedef {Object} CurriculumAssignment
 * User-facing canonical term: Course assigned to a Section.
 * Stored Section field remains classId in this pass.
 * @property {string} studentUid
 * @property {string} classId
 * @property {FirestoreTimestamp} assignedAt
 * @property {FirestoreTimestamp|null} dueDate
 * @property {FirestoreTimestamp|null} revokedAt
 */

/**
 * @typedef {Object} LessonSizingBreakdownItem
 * @property {number} lessonIndex
 * @property {boolean} isFinalAssessment
 * @property {number} quizMinutes
 * @property {number} netInstructionalMinutes
 * @property {number} targetWords
 */

/**
 * @typedef {Object} LessonSizingSnapshot
 * @property {number} wordsPerMinute
 * @property {number} totalTargetWords
 * @property {number} totalInstructionalPeriods
 * @property {number} totalAssessmentPeriods
 * @property {number} averageWordsPerLesson
 * @property {number} totalInstructionalMinutes
 * @property {LessonSizingBreakdownItem[]} breakdown
 * @property {FirestoreTimestamp} computedAt
 */

/**
 * @typedef {'remember'|'understand'|'apply'|'analyze'|'evaluate'|'create'} LotsogBloomsLevel
 */

/**
 * @typedef {Object} LotsogBlueprintLesson
 * @property {number} lessonIndex
 * @property {string} title
 * @property {boolean} isFinalAssessment
 * @property {number} targetWords
 * @property {string[]} topicOutline
 * @property {string} contentDepthGuidance
 * @property {string} priorKnowledgeAssumptions
 * @property {string} bridgeToNext
 * @property {string[]} keyVocabulary
 * @property {LotsogBloomsLevel} bloomsLevel
 */

/**
 * @typedef {'teacher'|'self_serve'} LotsogBlueprintGenerationFlow
 */

/**
 * @typedef {Object} LotsogCourseBlueprint
 * @property {FirestoreTimestamp} generatedAt
 * @property {number} totalTargetWords
 * @property {number} actualDistributedWords
 * @property {LotsogBlueprintGenerationFlow} generationFlow
 * @property {LotsogBlueprintLesson[]} lessons
 */

/**
 * @typedef {Object} CoursePreparationAssessmentConfig
 * @property {LotsogQuizFrequency} quizFrequency
 * @property {number|null} quizFrequencyN
 * @property {LotsogQuizType} quizType
 * @property {boolean} includesFinalAssessment
 * @property {LotsogFinalAssessmentType|null} finalAssessmentType
 * @property {number} masteryThresholdPercent
 */

/**
 * @typedef {Object} CoursePreparationDifferentiationSettings
 * @property {boolean} ellSupport
 * @property {string|null} iepAccommodations
 * @property {LotsogReadingLevelAdjustment} readingLevelAdjustment
 */

/**
 * @typedef {Object} CoursePreparationConfig
 * @property {string} [courseName]
 * @property {string|null} [bookIsbn]
 * @property {string|null} [bookTitle]
 * @property {string|null} [bookAuthor]
 * @property {string} [standardsFrameworkId]
 * @property {string} [standardsStageId]
 * @property {string[]} [standardsSubjects]
 * @property {string|null} [standardsNotes]
 * @property {number} [totalPeriods]
 * @property {number} [periodDurationMinutes]
 * @property {number} [meetingsPerWeek]
 * @property {FirestoreTimestamp|null} [startDate]
 * @property {FirestoreTimestamp|null} [endDate]
 * @property {FirestoreTimestamp[]} [skipDates]
 * @property {string} [gradeLevel]
 * @property {LotsogProficiencyLevel} [proficiencyLevel]
 * @property {LotsogNarrationStyle} [narrationStyle]
 * @property {CoursePreparationAssessmentConfig} [assessmentConfig]
 * @property {CoursePreparationDifferentiationSettings} [differentiationSettings]
 * @property {string[]} [prerequisites]
 * @property {LessonSizingSnapshot|null} [lessonSizing]
 */

/**
 * @typedef {Object} LotsogLearnerAssessmentConfig
 * @property {LotsogQuizFrequency} quizFrequency
 * @property {number|null} quizFrequencyN
 * @property {LotsogQuizType} quizType
 * @property {number} masteryThresholdPercent
 */

/**
 * @typedef {Object} LotsogLearnerConfig
 * @property {string|null} gradeLevel
 * @property {LotsogProficiencyLevel} proficiencyLevel
 * @property {number} periodDurationMinutes
 * @property {LotsogNarrationStyle} narrationStyle
 * @property {LotsogLearnerAssessmentConfig} assessmentConfig
 * @property {LessonSizingSnapshot|null} lessonSizing
 */

export const COURSE_PREPARATION_CONFIG_DEFAULTS = {
  standardsFrameworkId: DEFAULT_STANDARDS_FRAMEWORK_ID,
  standardsStageId: DEFAULT_STANDARDS_STAGE_ID,
  standardsSubjects: [],
  standardsNotes: null,
  periodDurationMinutes: 45,
  meetingsPerWeek: 5,
  narrationStyle: DEFAULT_NARRATION_STYLE,
  assessmentConfig: {
    quizFrequency: 'per_unit',
    quizFrequencyN: null,
    quizType: 'multiple_choice',
    includesFinalAssessment: true,
    finalAssessmentType: 'test',
    masteryThresholdPercent: 70,
  },
  differentiationSettings: {
    ellSupport: false,
    iepAccommodations: null,
    readingLevelAdjustment: 'on_grade',
  },
  prerequisites: [],
  skipDates: [],
};

export const LEARNER_CONFIG_DEFAULTS = {
  // Intentionally null by default; this must be set before startReviewLearningGeneration.
  gradeLevel: null,
  proficiencyLevel: 'intermediate',
  periodDurationMinutes: 45,
  narrationStyle: 'natural',
  assessmentConfig: {
    quizFrequency: 'per_unit',
    quizFrequencyN: null,
    quizType: 'multiple_choice',
    masteryThresholdPercent: 70,
  },
};

const buildCoursePreparationDefaults = () => ({
  ...COURSE_PREPARATION_CONFIG_DEFAULTS,
  standardsSubjects: [...COURSE_PREPARATION_CONFIG_DEFAULTS.standardsSubjects],
  prerequisites: [...COURSE_PREPARATION_CONFIG_DEFAULTS.prerequisites],
  skipDates: [...COURSE_PREPARATION_CONFIG_DEFAULTS.skipDates],
  assessmentConfig: { ...COURSE_PREPARATION_CONFIG_DEFAULTS.assessmentConfig },
  differentiationSettings: { ...COURSE_PREPARATION_CONFIG_DEFAULTS.differentiationSettings },
});

const buildLearnerDefaults = () => ({
  ...LEARNER_CONFIG_DEFAULTS,
  assessmentConfig: { ...LEARNER_CONFIG_DEFAULTS.assessmentConfig },
});

export function buildLearnerConfigFromCoursePreparation(coursePreparationConfig, options = {}) {
  const courseDefaults = buildCoursePreparationDefaults();
  const learnerDefaults = buildLearnerDefaults();
  const prep = coursePreparationConfig || {};
  const prepAssessment = {
    ...courseDefaults.assessmentConfig,
    ...(prep.assessmentConfig || {}),
  };
  return {
    gradeLevel: options.gradeLevel ?? prep.gradeLevel ?? null,
    proficiencyLevel: prep.proficiencyLevel ?? learnerDefaults.proficiencyLevel,
    periodDurationMinutes: Number(prep.periodDurationMinutes)
      || learnerDefaults.periodDurationMinutes,
    narrationStyle: prep.narrationStyle ?? learnerDefaults.narrationStyle,
    assessmentConfig: {
      quizFrequency: prepAssessment.quizFrequency,
      quizFrequencyN: prepAssessment.quizFrequencyN ?? null,
      quizType: prepAssessment.quizType,
      masteryThresholdPercent: Number(prepAssessment.masteryThresholdPercent)
        || learnerDefaults.assessmentConfig.masteryThresholdPercent,
    },
    lessonSizing: prep.lessonSizing ?? null,
  };
}

/**
 * @typedef {Object} LotsogAssignedStudentTrack
 * User-facing canonical term: Course (teacher-assigned to student).
 * Internal term: Track in users/{uid}/lotsogLearningTracks.
 * @property {string} trackId
 * @property {'teacher_assigned'} source
 * @property {string} teacherUid
 * @property {string} curriculumId
 * @property {string} classId
 * @property {string} subject
 * @property {LotsogCurriculumLevel} level
 * @property {number} days
 * @property {number} currentDay
 * @property {FirestoreTimestamp|null} dueDate
 * @property {FirestoreTimestamp|null} overdueNotifiedAt
 * @property {LotsogLearnerConfig|null} learnerConfig
 * @property {FirestoreTimestamp} createdAt
 * @property {FirestoreTimestamp} updatedAt
 */

/**
 * @typedef {Object} LotsogLearningTrack
 * @property {string} trackId
 * @property {string} source
 * @property {string} subject
 * @property {LotsogCurriculumLevel} level
 * @property {number} days
 * @property {number} currentDay
 * @property {LotsogLearnerConfig|null} learnerConfig
 * @property {LotsogCourseBlueprint} [blueprint]
 * @property {LotsogBlueprintStatus} [blueprintStatus]
 * @property {string|null} [blueprintError]
 * @property {FirestoreTimestamp} createdAt
 * @property {FirestoreTimestamp} updatedAt
 */

/**
 * @typedef {Object} LotsogTeacherCurriculum
 * User-facing canonical term: Course (teacher-authored reusable template).
 * Stored in users/{uid}/lotsogTeacherCurricula with id/key field curriculumId.
 * @property {string} curriculumId
 * @property {string} title
 * @property {string} description
 * @property {string} subject
 * @property {LotsogCurriculumLevel} level
 * @property {number} days
 * @property {LotsogCurriculumSourceMode} sourceMode
 * @property {CurriculumSourceMetadata|null} sourceMetadata
 * @property {LotsogSourceToc|null} [sourceToc]
 * @property {CurriculumSyllabus|null} syllabus
 * @property {Record<string, CurriculumLesson>} lessons
 * @property {LotsogCurriculumStatus} status
 * @property {string|null} errorMessage
 * @property {FirestoreTimestamp} createdAt
 * @property {FirestoreTimestamp} updatedAt
 * @property {string} teacherUid
 * @property {string} adminUid
 * @property {CurriculumAssignment[]} assignedTo
 * @property {FirestoreTimestamp|null} [reviewedAt]
 * @property {FirestoreTimestamp|null} [approvedAt]
 * @property {string|null} [approvedBy]
 * @property {CoursePreparationConfig} [coursePreparationConfig]
 * @property {LotsogCourseBlueprint} [blueprint]
 * @property {LotsogBlueprintStatus} [blueprintStatus]
 * @property {string|null} [blueprintError]
 * @property {LotsogSyllabusOverviewEntry[]} [syllabusOverview]
 */

/**
 * @typedef {'student'|'teacher'|'admin'|'manager'} LotsogSubscriptionRole
 */

/**
 * @typedef {'trial'|'small'|'medium'|'large'|'enterprise'} LotsogSubscriptionTier
 */

/**
 * @typedef {'trial'|'active'|'past_due'|'cancelled'} LotsogSubscriptionStatus
 */

/**
 * @typedef {'per_unit'|'blocked'} OverageBillingModel
 */

/**
 * @typedef {Object} SubscriptionLimits
 * @property {number} maxCourses
 * @property {number} maxTopics
 * @property {number} briefingsPerMonth
 * @property {number} realtimeMinutesPerMonth
 * @property {number} storageGb
 * @property {number} studentsPerTeacher
 * @property {number} teachersPerAdmin
 * @property {number} classesPerTeacher
 */

/**
 * @typedef {Object} OveragePolicy
 * @property {boolean} allowed
 * @property {OverageBillingModel} billingModel
 */

/**
 * @typedef {Object} LotsogSubscription
 * @property {LotsogSubscriptionRole} role
 * @property {LotsogSubscriptionTier} tier
 * @property {LotsogSubscriptionStatus} status
 * @property {FirestoreTimestamp|null} trialEndsAt
 * @property {FirestoreTimestamp} currentPeriodStart
 * @property {FirestoreTimestamp} currentPeriodEnd
 * @property {string|null} stripeCustomerId
 * @property {string|null} stripeSubscriptionId
 * @property {SubscriptionLimits} limits
 * @property {OveragePolicy} overage
 */

/**
 * @typedef {Object} LotsogUsageCurrentPeriod
 * @property {FirestoreTimestamp} periodStart
 * @property {FirestoreTimestamp} periodEnd
 * @property {number} briefingsGenerated
 * @property {number} coursesCreated
 * @property {number} realtimeMinutesUsed
 * @property {number} storageUsedGb
 * @property {number} studentsEnrolled
 * @property {number} teachersManaged
 * @property {FirestoreTimestamp} lastUpdated
 */

/**
 * @typedef {'briefing_generated'|'course_created'|'realtime_session'|'file_uploaded'|'student_enrolled'|'course_assigned'} LotsogUsageEventType
 */

/**
 * @typedef {Object} LotsogUsageEvent
 * @property {string} eventId
 * @property {LotsogUsageEventType} type
 * @property {FirestoreTimestamp} timestamp
 * @property {Record<string, unknown>} metadata
 * @property {number} units
 * @property {string} periodId
 */

/**
 * @typedef {Object} LotsogTeacherStudentIndex
 * @property {string} indexId
 * @property {string} teacherUid
 * @property {string} studentUid
 * @property {string} classId
 * @property {FirestoreTimestamp} enrolledAt
 * @property {string} adminUid
 */
